import React, { useEffect, useState } from "react";
import { CreditCard, Check, Sparkles, Gift, Settings, ExternalLink, Download } from "lucide-react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function Billing() {
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/billing/summary");
      setSummary(data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadSummary();
  }, [user]);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/subscribe", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPortalLoading(false);
    }
  };

  const redeemPromo = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: promoCode.trim() });
      toast.success(data.message || "Code redeemed");
      setPromoCode("");
      await loadSummary();
      if (refresh) await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRedeeming(false);
    }
  };

  const screens = summary?.screens_owned ?? 0;
  const firstPrice = summary?.first_screen_price ?? 9.99;
  const extraPrice = summary?.extra_screen_price ?? 4.99;
  const monthlyCost = summary?.monthly_cost_estimate ?? firstPrice;
  const hasSub = !!summary?.has_active_subscription;
  const subStatus = summary?.subscription_status;
  const periodEnd = summary?.current_period_end;
  const isPro = (summary?.effective_plan || user?.effective_plan) === "pro";
  const isTrial = summary?.is_trial ?? user?.is_trial;
  const trialDays = summary?.trial_days_left;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-5xl text-slate-900">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Billing</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
            Plan &amp;{" "}
            <span style={accentFont} className="text-sky-600">subscription</span>.
          </h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            7 days free, then ${firstPrice.toFixed(2)}/month for your first screen + ${extraPrice.toFixed(2)}/month for each additional screen. Cancel anytime.
          </p>
        </div>

        {/* Current plan card */}
        <div
          className="rounded-2xl p-6 mb-6 border backdrop-blur-xl flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }}
          data-testid="current-plan-card"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium mb-1">Current plan</div>
            <div className="text-2xl font-semibold tracking-tight flex flex-wrap items-center gap-2" style={headingFont}>
              {hasSub && isPro ? "Pro · subscribed" : isTrial ? "Free trial · Pro features" : isPro ? "Pro" : "Trial expired"}
              {hasSub && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="sub-status-pill">
                  <Sparkles className="h-3 w-3" /> {subStatus || "Active"}
                </span>
              )}
              {!hasSub && isTrial && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-rose-500 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full" data-testid="trial-pill">
                  <Sparkles className="h-3 w-3" /> {trialDays} day{trialDays === 1 ? "" : "s"} left
                </span>
              )}
            </div>
            <div className="text-sm text-slate-500 mt-1" data-testid="screens-used-row">
              {screens} screen{screens === 1 ? "" : "s"} paired
              {isPro && (
                <span className="ml-2 text-slate-700 font-medium">
                  · ~${monthlyCost.toFixed(2)}/month
                </span>
              )}
              {hasSub && periodEnd && (
                <span className="ml-2 text-sky-600">· next invoice {fmtDate(periodEnd)}</span>
              )}
            </div>
          </div>
          {hasSub && (
            <Button
              onClick={openPortal}
              disabled={portalLoading}
              variant="outline"
              className="rounded-full h-11 border-slate-300 hover:bg-slate-50"
              data-testid="manage-subscription-button"
            >
              <Settings className="h-4 w-4 mr-2" /> {portalLoading ? "Opening…" : "Manage subscription"}
            </Button>
          )}
        </div>

        {/* Free / Pro plan cards */}
        <div className="grid grid-cols-1 gap-5">
          {/* Pro */}
          <div
            className="rounded-3xl p-8 border backdrop-blur-xl flex flex-col relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(2,132,199,0.05))", borderColor: isPro ? "rgba(14,165,233,0.6)" : "rgba(14,165,233,0.35)", boxShadow: "0 0 40px rgba(14,165,233,0.15)" }}
            data-testid="plan-pro"
          >
            <div className="absolute top-0 right-0 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold bg-gradient-to-r from-sky-400 to-sky-600 text-white rounded-bl-2xl">
              7-day free trial
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-sky-600 mb-2 font-medium">Pro</div>
            <div className="flex items-baseline gap-2 mb-1">
              <div className="text-6xl tracking-tight" style={headingFont}>${firstPrice.toFixed(2)}</div>
              <div className="text-xs text-slate-500">/month base</div>
            </div>
            <div className="text-xs text-slate-500 mb-1">+ ${extraPrice.toFixed(2)} / extra screen / month</div>
            {isPro && (
              <div className="text-xs text-slate-700 mb-5 font-medium">
                Your current rate: <span className="text-sky-600">${monthlyCost.toFixed(2)}/month</span> for {screens} screen{screens === 1 ? "" : "s"}
              </div>
            )}
            <ul className="space-y-2 text-sm text-slate-800 mb-6 mt-4">
              <li className="flex gap-2"><Check className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" /><span>1 screen included, add more anytime</span></li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" /><span>Screen groups (multi-location)</span></li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" /><span>Time-based scheduling &amp; flash takeovers</span></li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" /><span>No &ldquo;Powered by Nexso&rdquo; ribbon on your TV</span></li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" /><span>Apps &amp; Widgets · Banner Studio · AI image generation</span></li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" /><span>Priority support</span></li>
            </ul>
            {hasSub ? (
              <Button disabled className="rounded-full mt-auto h-11 bg-sky-500/40 text-white font-semibold" data-testid="plan-pro-button">
                Current plan
              </Button>
            ) : (
              <Button onClick={subscribe} disabled={loading} className="rounded-full mt-auto h-11 bg-sky-500 hover:bg-sky-600 text-white shadow-sm font-semibold" data-testid="upgrade-button">
                <CreditCard className="h-4 w-4 mr-1.5" />
                {loading ? "Redirecting…" : isTrial ? "Subscribe to keep Pro" : "Start subscription"}
              </Button>
            )}
          </div>
        </div>

        {/* Download stick app banner */}
        <Link
          to="/download"
          className="mt-8 block rounded-2xl p-5 border bg-gradient-to-r from-sky-50 to-white hover:from-sky-100 transition-colors group"
          style={{ borderColor: "#BAE6FD" }}
          data-testid="download-launcher-link"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center shadow-sm">
                <Download className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-lg font-semibold tracking-tight" style={headingFont}>Install Nexso on your TV</div>
                <div className="text-sm text-slate-600">Download the Android launcher APK and sideload it to any Android stick or Smart TV.</div>
              </div>
            </div>
            <ExternalLink className="h-5 w-5 text-sky-600 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </Link>

        {/* Promo code redemption */}
        <div className="rounded-2xl p-6 mt-6 border backdrop-blur-xl" style={{ background: "linear-gradient(120deg, rgba(236,72,153,0.10), rgba(139,92,246,0.08))", borderColor: "rgba(236,72,153,0.30)", boxShadow: "0 0 30px rgba(236,72,153,0.15)" }} data-testid="promo-redeem-card">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 h-12 w-12 rounded-2xl bg-gradient-to-br from-pink-500 to-sky-600 flex items-center justify-center shadow-[0_0_24px_rgba(236,72,153,0.4)]">
              <Gift className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-semibold tracking-tight mb-1" style={headingFont}>Got a Nexso Stick promo code?</div>
              <div className="text-sm text-slate-600 mb-4">Enter the code printed inside your Nexso Stick box for 1 month of free Pro on top of your trial.</div>
              <form onSubmit={redeemPromo} className="flex flex-col sm:flex-row gap-2" data-testid="promo-form">
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="NEXSO-STICK-1MO"
                  className="rounded-xl h-11 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 font-mono tracking-wider uppercase"
                  data-testid="promo-input"
                />
                <Button
                  type="submit"
                  disabled={redeeming || !promoCode.trim()}
                  className="rounded-full h-11 px-6 bg-sky-500 hover:bg-sky-600 text-white shadow-sm font-semibold"
                  data-testid="promo-redeem-button"
                >
                  {redeeming ? "Redeeming…" : "Redeem code"}
                </Button>
              </form>
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-400 mt-6" data-testid="billing-disclaimer">
          Billing handled securely via Stripe. Cancel anytime from your dashboard. Test mode active — use card 4242 4242 4242 4242 with any future expiry and any 3-digit CVC.
        </div>
      </div>
    </DashboardLayout>
  );
}
